import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, clearTokens, hasTokens, saveTokens, type Tokens } from './api.js';

/**
 * Authenticated-user context.
 *
 * Bearer-token flow (see api.ts). On mount:
 *  - if no tokens in storage → treat user as signed out immediately.
 *  - otherwise call /users/me; the fetch wrapper will auto-refresh
 *    the access token once if needed.
 * `login` / `register` save the tokens returned by the API before
 * flipping the auth state to signed-in.
 */

export interface AuthUser {
  id: string;
  email: string;
  role: 'OWNER' | 'PROPERTY_MANAGER' | 'ACCOUNTANT' | 'VIEWER';
  isActive: boolean;
  lastLoginAt: number | null;
  createdAt: number;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  register(email: string, password: string, invite?: string): Promise<void>;
  logout(): Promise<void>;
  refetch(): Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    // No token in storage → don't waste a network round trip.
    if (!hasTokens()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { user } = await api<{ user: AuthUser }>('/api/v1/users/me');
      setUser(user);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
        clearTokens();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refetch(); }, [refetch]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api<{ user: AuthUser; tokens: Tokens }>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuthRetry: true,
    });
    saveTokens(res.tokens);
    setUser(res.user);
  }, []);

  const register = useCallback(async (email: string, password: string, invite?: string) => {
    const res = await api<{ user: AuthUser; tokens: Tokens }>('/api/v1/auth/register', {
      method: 'POST',
      body: { email, password, ...(invite ? { invite } : {}) },
      skipAuthRetry: true,
    });
    saveTokens(res.tokens);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>('/api/v1/auth/logout', {
        method: 'POST', body: {}, skipAuthRetry: true,
      });
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, register, logout, refetch }),
    [user, loading, login, register, logout, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
